import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createBlock as _createBlock, Fragment as _Fragment, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = ["data-theme"]

import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import {
  useRoute,
} from 'vue-router';
import BusNotification from '@/components/App/BusNotification.vue';
import AppFooter from '@/components/App/Footer/AppFooter.vue';
import GoogleOneTap from '@/components/App/GoogleOneTap.vue';
import LazyScript from '@/components/App/LazyScript.vue';
import MediaModal from '@/components/App/MediaModal.vue';
import RouteImageModal from '@/components/App/RouteImageModal.vue';
import CustomLoader1 from '@/components/Helper/Loader/CustomLoader1.vue';
import AppHeader from '@/components/Navbar/AppHeader.vue';
import {
  IS_DEVELOPMENT,
  identifyDeviceType,
  viewportBreakpoint,
} from '@/helpers';
import {
  useStore,
} from '@/store';
import type {
  ICompany,
} from '@/types';


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {


const route = useRoute();

const appStore = useStore.app;
const navStore = useStore.nav;
const authStore = useStore.auth;
const backendStore = useStore.backend;

const features = ref<ICompany['subscriptionSettings']['features'] | undefined>();
const isRouteModalVisible = ref(true);

const routeModal = computed(() => {
  const { routeModals } = useStore.app.viewConfig;
  return routeModals.find((modal) => modal.paths.includes(route.path));
});

watch(() => route.path, () => {
  isRouteModalVisible.value = true;
});

watch(() => authStore.token, (newVal, oldVal) => {
  const triggerAuthChangeHandler = (newVal && !oldVal) || (oldVal && !newVal);
  if (triggerAuthChangeHandler) {
    backendStore.initOnAuthChange();
  }
});

watch(() => appStore.isBootstrapped, (newVal) => {
  if (!newVal) {
    return;
  }

  features.value = backendStore.clientCompany.subscriptionSettings.features;

  if (features.value.studentPortal) {
    _updateManifest();
  }
});

watch(
  () => appStore.styleConfig,
  () => {
    _applyDomainStyleConfig();
  },
  {
    deep: true,
  },
);

onBeforeMount(() => {
  window.addEventListener('storage', _storageEventHandler);
  window.addEventListener('resize', _resizedBreakpointEventHandler);

  if (!IS_DEVELOPMENT) {
    // disable right mouse button click in web page
    document.addEventListener('contextmenu', (event) => {
      event.preventDefault();
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('storage', _storageEventHandler);
  window.removeEventListener('resize', _resizedBreakpointEventHandler);
});

onMounted(() => {
  window.setTimeout(() => {
    if (features.value?.studentPortal) {
      _applyDomainStyleConfig();
      backendStore.initOnAppMount();
    }
  }, 2 * 1000);
});

const _storageEventHandler = (e: StorageEvent): void => {
  if (e.key === 'token') {
    // local storage token changed
    console.log(`Key Changed: ${e.key}`);
    window.sessionStorage.removeItem('token');
    window.location.reload();
  }
};

const _resizedBreakpointEventHandler = (e: Event): void => {
  const device = identifyDeviceType();
  appStore.breakpoint = viewportBreakpoint();
  appStore.device = device;
  appStore.windowWidth = window.innerWidth;
  appStore.windowHeight = window.innerHeight;
};

const _applyDomainStyleConfig = (): void => {
  const { colors } = appStore.styleConfig;
  Object.entries(colors).forEach(([key, value]) => {
    document.documentElement.style.setProperty(`--dynamic-${key}`, value);
  });
};

const _updateManifest = async (): Promise<void> => {
  const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;

  if (!manifestLink) {
    return;
  }

  try {
    interface IManifestIcon {
      src: string;
      sizes: string;
      type: string;
    }

    interface IManifest {
      name?: string;
      short_name?: string;
      start_url?: string;
      display?: string;
      background_color?: string;
      theme_color?: string;
      icons?: IManifestIcon[];
    }

    // Fetch the existing manifest
    const response = await fetch(manifestLink.href);
    const manifest: IManifest = await response.json();

    const { name, publicWebsite } = backendStore.clientCompany;
    const { iconsPath } = backendStore.clientCompany.imageSettings;

    manifest.name = name;
    manifest.short_name = name;
    manifest.start_url = publicWebsite;
    manifest.icons = manifest.icons?.map((icon) => ({
      ...icon,
      src: icon.src.replace('./img/icons', iconsPath),
    }));

    // Create a new Blob for the updated manifest
    const updatedManifestBlob = new Blob([JSON.stringify(manifest)], {
      type: 'application/json',
    });

    // Update the manifest link's href
    manifestLink.href = URL.createObjectURL(updatedManifestBlob);
  } catch (error) {
    console.error('Failed to update manifest:', error);
  }
};


return (_ctx: any,_cache: any) => {
  const _component_RouterView = _resolveComponent("RouterView")!

  return (features.value?.studentPortal === false)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1))
    : (_openBlock(), _createElementBlock("div", {
        key: 1,
        class: "relative flex min-h-screen flex-col justify-between bg-dynamicBg font-balooDa2",
        "data-theme": _unref(navStore).isLightTheme ? 'light' : 'dracula'
      }, [
        (_unref(appStore).isBootstrapped)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(LazyScript),
              (_unref(route).name !== 'UserExam')
                ? (_openBlock(), _createBlock(AppHeader, { key: 0 }))
                : _createCommentVNode("", true)
            ], 64))
          : _createCommentVNode("", true),
        _createElementVNode("main", {
          class: _normalizeClass(["grow", { 'mt-20': _unref(route).name !== 'UserExam' }])
        }, [
          (_unref(appStore).isBootstrapped)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(GoogleOneTap),
                _createVNode(BusNotification),
                _createVNode(MediaModal),
                (routeModal.value && isRouteModalVisible.value)
                  ? (_openBlock(), _createBlock(RouteImageModal, {
                      key: 0,
                      routeModal: routeModal.value,
                      onClose: _cache[0] || (_cache[0] = ($event: any) => (isRouteModalVisible.value = false))
                    }, null, 8, ["routeModal"]))
                  : _createCommentVNode("", true)
              ], 64))
            : _createCommentVNode("", true),
          _createElementVNode("div", null, [
            (_unref(appStore).isLoading || !_unref(appStore).isBootstrapped)
              ? (_openBlock(), _createBlock(CustomLoader1, {
                  key: 0,
                  class: "modal modal-open"
                }))
              : _createCommentVNode("", true),
            (_unref(appStore).isBootstrapped)
              ? (_openBlock(), _createBlock(_component_RouterView, { key: 1 }))
              : _createCommentVNode("", true)
          ])
        ], 2),
        (_unref(appStore).isBootstrapped)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (!_unref(appStore).isLoading && _unref(route).name !== 'UserExam')
                ? (_openBlock(), _createBlock(AppFooter, { key: 0 }))
                : _createCommentVNode("", true)
            ], 64))
          : _createCommentVNode("", true)
      ], 8, _hoisted_2))
}
}

})